import { z } from "zod";

import type {
	CreateHotelPromoBody,
	HotelPromoTarget,
	UpdateHotelPromoBody,
} from "@/features/hotel-promos/types";
import i18n from "@/i18n";

const CODE_PATTERN = /^[A-Za-z0-9._-]+$/;
const PHONE_PATTERN = /^\+380\d{9}$/;

export type HotelPromoFormValues = {
	code: string;
	phone: string;
	percent: number;
	isActive: boolean;
	partnerId: string;
	categoryId: string;
	hotelKeys: string[];
	targetType: HotelPromoTarget;
};

export function emptyHotelPromoValues(
	targetType: HotelPromoTarget = "CATEGORY",
): HotelPromoFormValues {
	return {
		code: "",
		phone: "",
		percent: 10,
		isActive: true,
		partnerId: "",
		categoryId: "",
		hotelKeys: [],
		targetType,
	};
}

export function hotelPromoSchema(mode: "create" | "edit") {
	return z
		.object({
			code: z
				.string()
				.trim()
				.min(1, i18n.t("hotel.codeRequired"))
				.max(50, i18n.t("hotel.codeInvalid"))
				.regex(CODE_PATTERN, i18n.t("hotel.codeInvalid")),
			phone: z.string(),
			percent: z
				.number({
					required_error: i18n.t("hotel.percentRequired"),
					invalid_type_error: i18n.t("hotel.percentRequired"),
				})
				.int()
				.min(1, i18n.t("hotel.percentRequired"))
				.max(100, i18n.t("hotel.percentRequired")),
			isActive: z.boolean(),
			partnerId: z.string(),
			categoryId: z.string(),
			hotelKeys: z.array(z.string()).min(1, i18n.t("hotel.hotelsRequired")),
			targetType: z.enum(["CATEGORY", "USER", "PARTNER"]),
		})
		.superRefine((values, ctx) => {
			if (mode === "edit") {
				return;
			}
			if (values.targetType === "CATEGORY" && !values.categoryId) {
				ctx.addIssue({
					code: "custom",
					path: ["categoryId"],
					message: i18n.t("hotel.categoryRequired"),
				});
			}
			if (values.targetType === "USER" && !PHONE_PATTERN.test(values.phone)) {
				ctx.addIssue({
					code: "custom",
					path: ["phone"],
					message: i18n.t("hotel.phoneRequired"),
				});
			}
			if (values.targetType === "PARTNER" && !values.partnerId) {
				ctx.addIssue({
					code: "custom",
					path: ["partnerId"],
					message: i18n.t("hotel.partnerRequired"),
				});
			}
		});
}

export function createHotelPromoBody(
	values: HotelPromoFormValues,
): CreateHotelPromoBody {
	return {
		code: values.code.trim(),
		phone: values.targetType === "USER" ? values.phone : undefined,
		percent: values.percent,
		isActive: values.isActive,
		partnerId: values.targetType === "PARTNER" ? values.partnerId : undefined,
		categoryId:
			values.targetType === "CATEGORY" ? values.categoryId : undefined,
		hotelKeys: values.hotelKeys,
		targetType: values.targetType,
	};
}

export function updateHotelPromoBody(
	values: HotelPromoFormValues,
): UpdateHotelPromoBody {
	return {
		code: values.code.trim(),
		percent: values.percent,
		isActive: values.isActive,
		hotelKeys: values.hotelKeys,
	};
}
